<template>
  <v-app class="app">
    <v-app-bar class="flex-grow-0" height="60">
      <v-container fluid class="d-flex pa-0 align-center">
        <!-- Logo -->
        <div class="d-inline-flex align-center logo">
          <router-link :to="{ name: 'index' }">
            <v-img
              :src="require('@/assets/logo.svg')"
              class="logo flex-grow-0"
              height="55"
              width="55"
              contain
            />
          </router-link>
          <v-divider class="mx-3" vertical></v-divider>
          <a
            href="https://www.spbstu.ru/abit/master/"
            rel="noopener noreferrer"
            target="_blank"
            class="text-no-wrap abit"
          >
            {{ $t("layout.navbar.abit") }} <span>{{ currentYear }}</span>
          </a>
        </div>

        <v-spacer></v-spacer>

        <!-- Actions -->
        <slot v-if="showActions">
          <v-tabs
            v-if="$vuetify.breakpoint.mdAndUp"
            align-with-title
            hide-slider
            optional
          >
            <v-tab exact :to="{ name: 'news' }">
              <v-icon left>$mdi-newspaper</v-icon>
              {{ $t("layout.navbar.news") }}
            </v-tab>

            <v-tab class="register" exact :to="registerLink">
              <v-btn class="primary" text :ripple="false" :disabled="!isOpen">{{
                $t("layout.navbar.register")
              }}</v-btn>
            </v-tab>
          </v-tabs>

          <NavButton />
        </slot>

        <!-- Admin menu -->
        <slot v-if="showAdminActions">
          <v-tabs
            v-if="$vuetify.breakpoint.smAndUp"
            align-with-title
            hide-slider
            optional
          >
            <v-tab exact :to="{ name: 'apps-list' }"
              ><v-icon left>$mdi-file-multiple</v-icon>
              {{ $t("layout.navbar.apps") }}</v-tab
            >
            <v-tab
              v-if="$vuetify.breakpoint.mdAndUp"
              exact
              :to="{ name: 'stats' }"
              ><v-icon left>$mdi-chart-bar</v-icon>
              {{ $t("layout.navbar.stats") }}</v-tab
            >
            <slot v-if="$vuetify.breakpoint.lgAndUp">
              <v-tab
                v-if="$auth.user().roles.includes('admin')"
                exact
                :to="{ name: 'moderators-list' }"
                ><v-icon left>$mdi-account-multiple</v-icon>
                {{ $t("layout.navbar.moderators") }}</v-tab
              >
              <v-tab
                v-if="$auth.user().roles.includes('admin')"
                exact
                :to="{ name: 'settings' }"
                ><v-icon left>$mdi-cog</v-icon>
                {{ $t("layout.navbar.settings") }}</v-tab
              >
            </slot>
          </v-tabs>

          <NavAdminButton />
          <AccountButton />
        </slot>
      </v-container>
    </v-app-bar>

    <!-- Main block -->
    <v-main v-if="loaded" style="margin-top: 28px; margin-bottom: 100px;">
      <router-view />
    </v-main>

    <!-- Footer -->
    <v-footer app absolute style="background:#00392a" dark padless>
      <v-container>
        <v-row no-gutters>
          <v-col cols="12" md="6">
            <div class="font-weight-bold">
              {{ new Date().getFullYear() }} —
              {{ $t("layout.footer.title") }}
            </div>
            <a
              href="https://www.spbstu.ru/upload/personal_data_policy.pdf"
              rel="noopener noreferrer"
              target="_blank"
              >{{ $t("layout.footer.privacypolicy") }}</a
            >
            <div class="muted--text">{{ $t("layout.footer.copyright") }}</div>
            <div>
              <a
                class="d-inline-block mr-2"
                href="https://www.spbstu.ru/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img :src="require('@/assets/logo_grey.svg')" width="30" />
              </a>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="font-weight-bold">
              {{ $t("layout.footer.contacts") }}
            </div>
            <a
              href="https://goo.gl/maps/LGjt2hBHsbq"
              target="_blank"
              rel="noopener noreferrer"
              ><v-icon small class="mr-1">$mdi-map</v-icon
              >{{ $t("layout.footer.address") }}</a
            >
            <a href="tel:+78125527765"
              ><v-icon small class="mr-1">$mdi-phone</v-icon
              >{{ $t("layout.footer.phone") }}</a
            >
            <a href="mailto:enter@spbstu.ru"
              ><v-icon small class="mr-1">$mdi-email</v-icon
              >{{ $t("layout.footer.email") }}</a
            >
            <div style="color:#00392a; position:absolute; right:0;bottom:0;">
              ©LIK
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>

    <!-- Cookies -->
    <cookies-agreement></cookies-agreement>

    <!-- Helper global modules -->
    <confirm ref="confirm"></confirm>
    <info ref="info"></info>
    <alert ref="alert"></alert>
  </v-app>
</template>

<script>
import alert from "@/components/AlertPanel";
import confirm from "@/components/ConfirmDialog";
import info from "@/components/InfoDialog";
import axios from "axios";
import AccountButton from "./components/AccountButton.vue";
import NavButton from "./components/NavButton.vue";
import NavAdminButton from "./components/NavAdminButton.vue";
import CookiesAgreement from "./components/CookiesAgreement.vue";

export default {
  name: "App",

  components: {
    confirm,
    info,
    alert,
    AccountButton,
    NavButton,
    NavAdminButton,
    CookiesAgreement,
  },

  data() {
    return {
      loaded: false,
    };
  },

  computed: {
    showActions() {
      const allowed = ["index", "grants", "news"];
      return !!this.$route.name && allowed.includes(this.$route.name);
    },

    showAdminActions() {
      const notAllowed = ["index", "grants", "news", "register", "editing"];
      if (notAllowed.includes(this.$route.name)) return false;

      return !!this.$route.name && this.$auth.ready() && !!this.$auth.user();
    },

    isOpen() {
      return this.$store.state.pars.isOpen;
    },

    registerLink() {
      if (!this.isOpen) return { name: "index" };
      return { name: "register" };
    },

    currentYear() {
      return new Date().getFullYear();
    },
  },

  async mounted() {
    // Global dialogs
    this.$root.$confirm = this.$refs.confirm.open;
    this.$root.$info = this.$refs.info.open;

    // Global alerts
    this.$root.$alert = this.$refs.alert.add;
    this.$router.beforeEach((to, from, next) => {
      this.$refs.alert?.clear();
      next();
    });

    // Global interceptor for net error
    axios.interceptors.response.use(undefined, error => {
      if (error.message === "Network Error") {
        this.$root.$alert("error", this.$t("error.net.desc"));
      }
      return Promise.reject(error);
    });

    // Load pars
    try {
      const res = await this.$http.get("/pars");
      this.$store.commit("pars/init", res.data);
      this.loaded = true;
    } catch (e) {
      this.HandleError(e);
    }
  },
};
</script>

<style lang="scss">
.app {
  .alert-panel {
    position: fixed;
    right: 0;
    top: 10px;
    width: 500px;
    z-index: 300;

    @media only screen and (max-width: 499px) {
      width: 100%;
    }
  }

  // Styling
  .v-app-bar {
    &.theme--light.v-toolbar.v-sheet {
      background-color: #fff;
    }

    &.v-sheet.v-toolbar {
      box-shadow: 0px 2px 3px 0px #0000001a, 0px 5px 10px 0px #0000001a;
    }

    // Top menu container
    .container {
      max-width: 1128px;

      // Menu links
      a {
        text-decoration-line: none;
        font-size: 15px;
        letter-spacing: -0.03em;
        color: #7e7e7e;
        text-transform: none;

        &.disabled {
          cursor: not-allowed;
        }
      }

      a:hover,
      a:active {
        color: #37b34a;
      }

      .logo {
        .v-divider {
          border-color: rgba(0, 0, 0, 0.08);
        }

        .abit {
          font-weight: 600;
          font-size: 16px;
          color: #585858;

          span {
            color: #37b34a;
          }
        }

        .abit:hover,
        .abit:active {
          color: #585858;
          opacity: 0.7;
        }
      }

      .v-tabs {
        .v-tabs-bar {
          .v-tab {
            color: #7e7e7e;

            &.v-tab--active {
              color: #008a15;
            }

            &:hover {
              color: #37b34a;

              path {
                fill: #37b34a;
              }
            }

            &.register {
              padding: 0;
              margin-left: 30px;

              .v-btn {
                width: 141px;
                height: 44px;
                box-shadow: 0px 2px 4px 0px #00000040;
                font-weight: 700;
                text-transform: none;
                color: #ffffff;

                &.v-btn--disabled {
                  background-color: #dddddd !important;
                }
              }
            }
          }
        }
      }
    }
  }

  @media only screen and (max-width: 1174px) {
    .v-app-bar {
      height: 69px !important;

      .v-toolbar__content {
        height: 69px !important;
        padding: 7px 20px 7px 20px;
      }

      .container {
        max-width: unset;

        // Menu actions
        .actions {
          gap: 42px;
        }
      }
    }
  }

  .v-app-bar {
    .v-tabs-bar__content {
      justify-content: flex-end;
    }
  }

  .v-footer {
    font-size: 0.8rem;
    font-weight: 300;

    .muted--text {
      color: #999;
    }

    .row {
      div {
        a,
        div {
          margin: 10px 0px;
        }
      }
    }

    a {
      display: block;
      text-decoration: none;
      color: #ccc;
      font-weight: 400;
    }

    a:hover,
    a:active {
      color: white;
    }
  }
}
</style>
